import React, { useState } from 'react';
import axios from 'axios';
import { Rocket, Mail, MapPin, Loader2, CheckCircle2 } from 'lucide-react';
import { BRAND, SERVICE_OPTIONS } from '../data/siteData';
import useReveal from '../hooks/useReveal';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { name: '', email: '', company: '', service: '', message: '' };

// Returns an error message when required fields are missing, otherwise null
function validateForm(form) {
  if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
    return 'Please fill in your name, email and message.';
  }
  return null;
}

function ContactSuccess({ onReset }) {
  return (
    <div className="contact-success" data-testid="contact-success">
      <CheckCircle2 size={48} />
      <h3>Transmission received!</h3>
      <p>Your message is on its way to Mission Control. We’ll be in touch shortly.</p>
      <button type="button" className="boost-btn boost-btn-ghost" onClick={onReset} data-testid="contact-send-another">
        Send another message
      </button>
    </div>
  );
}

function ContactFields({ form, set, sending }) {
  return (
    <>
      <div className="form-row">
        <div className="form-field">
          <label htmlFor="c-name">Name *</label>
          <input id="c-name" type="text" placeholder="Jane Astronaut" value={form.name} onChange={set('name')} data-testid="contact-input-name" />
        </div>
        <div className="form-field">
          <label htmlFor="c-email">Email *</label>
          <input id="c-email" type="email" placeholder="jane@galaxy.com" value={form.email} onChange={set('email')} data-testid="contact-input-email" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label htmlFor="c-company">Company</label>
          <input id="c-company" type="text" placeholder="Galaxy Ltd (optional)" value={form.company} onChange={set('company')} data-testid="contact-input-company" />
        </div>
        <div className="form-field">
          <label htmlFor="c-service">Mission type</label>
          <select id="c-service" value={form.service} onChange={set('service')} data-testid="contact-select-service">
            <option value="">Select a mission…</option>
            {SERVICE_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-field">
        <label htmlFor="c-message">Message *</label>
        <textarea id="c-message" rows={5} placeholder="Tell us about your brand and where you want it to go…" value={form.message} onChange={set('message')} data-testid="contact-input-message" />
      </div>
      <button type="submit" className="boost-btn boost-btn-primary boost-btn-lg contact-submit" disabled={sending} data-testid="contact-submit">
        {sending ? <Loader2 size={18} className="spin" /> : <Rocket size={18} />}
        {sending ? 'Launching…' : 'Send It To Orbit'}
      </button>
    </>
  );
}

function ContactIntro({ headRef }) {
  return (
    <div className="contact-copy reveal" ref={headRef}>
      <span className="section-eyebrow">// MISSION CONTROL</span>
      <h2 className="section-title">
        READY FOR <span className="text-pink">LAUNCH?</span>
      </h2>
      <p className="section-sub">
        Tell us about your brand and we’ll plot the flight path. No jargon, no fluff —
        just a crew that gets things off the ground.
      </p>
      <a href={`mailto:${BRAND.email}`} className="contact-email" data-testid="contact-email">
        <Mail size={18} />
        {BRAND.email}
      </a>
      <p className="contact-address" data-testid="contact-address">
        <MapPin size={17} />
        {BRAND.address}
      </p>
      <div className="contact-badge">
        <span className="pulse-dot" />
        Mission Control is online — replies within 24h
      </div>
    </div>
  );
}

export default function Contact() {
  const headRef = useReveal();
  const formRef = useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const error = validateForm(form);
    if (error) {
      toast({ title: 'Hold the countdown!', description: error });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(INITIAL);
      toast({ title: 'Message launched!', description: "We'll get back to you at light speed." });
    } catch (err) {
      console.error('Contact submit failed', err);
      toast({ title: 'Launch aborted', description: 'Something went wrong — please try again.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section contact-section" id="contact" data-testid="contact-section">
      <div className="section-inner">
        <div className="contact-grid">
          <ContactIntro headRef={headRef} />

          <form className="contact-form reveal" ref={formRef} onSubmit={submit} data-testid="contact-form">
            {sent ? (
              <ContactSuccess onReset={() => setSent(false)} />
            ) : (
              <ContactFields form={form} set={set} sending={sending} />
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
